import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { AlertController } from '@ionic/angular/standalone';
import { _, TranslatePipe } from '@ngx-translate/core';
import { filter, first, finalize, Subscription } from 'rxjs';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { PictureComponent } from 'src/app/components/picture/picture.component';
import { PriceComponent } from 'src/app/components/price/price.component';
import { LoadingUtils } from 'src/app/utils/loading.utils';
import { ModalContent } from '../../../../models/modal-content';
import { Offer } from '../../../../models/offer';
import { Paginated } from '../../../../models/papginated';
import { User } from '../../../../models/user';
import { AuthService } from '../../../../services/auth.service';
import { ModalService } from '../../../../services/modal.service';
import { OfferService } from '../../../../services/offer.service';
import { ToastService } from '../../../../services/toast.service';
import { TranslateService } from '../../../../services/translate.service';
import { CategoryUtils } from '../../../../utils/category.utils';
import { eidsAuthorizationActive, eidsKind } from '../../../../utils/eids.utils';

@Component({
    selector: 'app-profile-tab-offers',
    templateUrl: './profile-tab-offers.page.html',
    styleUrls: ['./profile-tab-offers.page.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, TranslatePipe, LoaderComponent, PriceComponent, PictureComponent, PaginationComponent],
})
export class ProfileTabOffersPage implements OnInit, OnDestroy {
    offers: Paginated<Offer> = { items: [], total: 0, page: 1, perPage: 25 };
    loadingOffers = true;
    user: User;
    /** 'all' | '0' | '1' — default hide archived */
    archivedFilter: string = '0';
    /** 'all' | '0' | '1' */
    publishedFilter: string = 'all';
    private routerSubscription: Subscription;

    constructor(
        private readonly offerService: OfferService,
        private readonly authService: AuthService,
        private readonly alertCtrl: AlertController,
        private readonly translateService: TranslateService,
        private readonly modalService: ModalService,
        private readonly toastService: ToastService,
        private readonly router: Router,
    ) {}

    ngOnInit() {
        this.authService.userLoggedIn
            .pipe(filter((user) => !!user))
            .pipe(first())
            .subscribe({
                next: (user) => {
                    this.user = user;
                    this.loadOffers(1);
                },
            });

        // Reload offers when navigating back to this page
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (event.urlAfterRedirects === '/profile/offers') {
                    this.loadOffers(this.offers.page);
                }
            });
    }

    ngOnDestroy() {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    get isDefaultFilters(): boolean {
        return this.archivedFilter === '0' && this.publishedFilter === 'all';
    }

    onFiltersChanged() {
        this.loadOffers(1);
    }

    pageChanged(page: number) {
        this.loadOffers(page);
    }

    private parseFilter(value: string): boolean | null {
        if (value === 'all') {
            return null;
        }
        return value === '1';
    }

    private loadOffers(page: number) {
        if (!this.user) {
            return;
        }
        this.loadingOffers = true;
        this.offerService
            .getUserOffers(this.user, {
                page,
                perPage: this.offers.perPage,
                archived: this.parseFilter(this.archivedFilter),
                published: this.parseFilter(this.publishedFilter),
            })
            .subscribe({
                next: (result) => {
                    if (result.items.length === 0 && result.page > 1 && result.total > 0) {
                        this.loadOffers(result.page - 1);
                        return;
                    }
                    this.offers = result;
                    this.loadingOffers = false;
                },
                error: (err) => {
                    console.log(err);
                    this.offers = { items: [], total: 0, page: 1, perPage: this.offers.perPage };
                    this.loadingOffers = false;
                },
            });
    }

    removeOffer(offer: Offer) {
        const content: ModalContent = {
            title: _('Remove offer'),
            message: _('Are you sure you want to remove this offer?'),
            confirmText: _('Remove'),
            cancelText: _('Cancel'),
            class: 'alert-modal h-500',
            callback: async (data) => {
                if (data.action === 'confirm') {
                    await LoadingUtils.show();
                    this.offerService.removeOffer(offer).subscribe({
                        next: () => {
                            this.toastService.success(_('Your offer has been removed, other users will no longer see it'));
                            this.loadOffers(this.offers.page);
                            LoadingUtils.hide();
                        },
                    });
                }
            },
        };
        this.modalService.create(content);
    }

    updateOffer(offer: Offer) {
        this.router.navigate(['/profile/offers', offer.id, 'edit']);
    }

    offerStatusLabel(offer: Offer): string {
        if (offer.validationStatus === 'rejected') {
            return 'offer_status_rejected';
        }
        if (offer.validationStatus === 'pending' || offer.pendingValidation) {
            return 'offer_status_pending_validation';
        }
        if (this.isArchived(offer)) {
            return 'offer_visibility_archived';
        }
        if (offer.isPublished) {
            return 'offer_visibility_online';
        }
        return 'offer_visibility_offline';
    }

    offerStatusBadgeClass(offer: Offer): string {
        const key = this.offerStatusLabel(offer);
        if (key === 'offer_visibility_online') {
            return 'bg-success';
        }
        if (key === 'offer_visibility_offline') {
            return 'bg-warning text-dark';
        }
        if (key === 'offer_visibility_archived') {
            return 'bg-dark';
        }
        return 'bg-secondary';
    }

    isArchived(offer: Offer): boolean {
        return !!offer.archivedAt;
    }

    canShowDeactivate(offer: Offer): boolean {
        return offer.isPublished && !this.isArchived(offer);
    }

    canShowActivate(offer: Offer): boolean {
        if (offer.isPublished || this.isArchived(offer)) {
            return false;
        }
        if (offer.validationStatus === 'rejected' || offer.validationStatus === 'pending' || offer.pendingValidation) {
            return false;
        }
        return true;
    }

    canArchive(offer: Offer): boolean {
        if (this.isArchived(offer)) {
            return false;
        }
        if (offer.validationStatus === 'pending' || offer.pendingValidation) {
            return false;
        }
        return true;
    }

    canUnarchive(offer: Offer): boolean {
        return this.isArchived(offer);
    }

    canReactivate(offer: Offer): boolean {
        return this.isArchived(offer);
    }

    async setPublished(offer: Offer, published: boolean) {
        const eids = published ? await this.askEidsAuthority(offer) : {};
        if (published && eids === null) {
            return;
        }
        await LoadingUtils.show();
        this.offerService
            .setPublished(offer.id, published, eids ?? undefined)
            .pipe(finalize(() => LoadingUtils.hide()))
            .subscribe({
                next: () => {
                    this.toastService.success(published ? _('Offer is now online') : _('Offer is now offline'));
                    this.loadOffers(this.offers.page);
                },
                error: (err) => this.handleOfferActionError(err),
            });
    }

    async archiveOffer(offer: Offer) {
        await LoadingUtils.show();
        this.offerService
            .archiveOffer(offer.id)
            .pipe(finalize(() => LoadingUtils.hide()))
            .subscribe({
                next: () => {
                    this.toastService.success(_('Offer archived'));
                    this.loadOffers(this.offers.page);
                },
                error: (err) => this.handleOfferActionError(err),
            });
    }

    async unarchiveOffer(offer: Offer) {
        await LoadingUtils.show();
        this.offerService
            .unarchiveOffer(offer.id)
            .pipe(finalize(() => LoadingUtils.hide()))
            .subscribe({
                next: () => {
                    this.toastService.success(_('Offer unarchived'));
                    this.loadOffers(this.offers.page);
                },
                error: (err) => this.handleOfferActionError(err),
            });
    }

    async reactivateOffer(offer: Offer) {
        const eids = await this.askEidsAuthority(offer);
        if (eids === null) {
            return;
        }
        await LoadingUtils.show();
        this.offerService
            .reactivateOffer(offer.id, eids)
            .pipe(finalize(() => LoadingUtils.hide()))
            .subscribe({
                next: () => {
                    this.toastService.success(_('Offer is now online'));
                    this.loadOffers(this.offers.page);
                },
                error: (err) => this.handleOfferActionError(err),
            });
    }

    private handleOfferActionError(err: { error?: { error?: string; details?: { code?: string } } }) {
        const code = err?.error?.error;
        const detailsCode = err?.error?.details?.code;
        if (code === 'Offer is archived') {
            this.toastService.error(_('offer_is_archived'));
            return;
        }
        if (typeof detailsCode === 'string' && detailsCode.startsWith('eids_') && typeof code === 'string') {
            this.toastService.error(code);
            return;
        }
        this.toastService.error(_('An error occurred'));
    }

    private async askEidsAuthority(offer: Offer): Promise<{ tasinmazId?: string; plakaNo?: string } | null | Record<string, never>> {
        const category = CategoryUtils.getById(offer.category);
        const kind = eidsKind(category, (id) => CategoryUtils.getById(id));
        if (!kind || eidsAuthorizationActive(offer.eidsAuthorization)) {
            return {};
        }
        const field = kind === 'property' ? 'tasinmazId' : 'plakaNo';
        const alert = await this.alertCtrl.create({
            header: this.translateService.get(_('eids.authority.prompt_title')),
            message: this.translateService.get(kind === 'property' ? _('eids.authority.property_help') : _('eids.authority.vehicle_help')),
            inputs: [
                {
                    name: 'value',
                    placeholder: this.translateService.get(kind === 'property' ? _('eids.authority.tasinmaz') : _('eids.authority.plaka')),
                },
            ],
            buttons: [
                { text: this.translateService.get(_('Cancel')), role: 'cancel' },
                { text: this.translateService.get(_('Continue')), role: 'ok' },
            ],
        });
        await alert.present();
        const result = await alert.onDidDismiss();
        if (result.role === 'cancel') {
            return null;
        }
        const value = String(result.data?.values?.value ?? '').trim();
        if (!value) {
            this.toastService.error(_(kind === 'property' ? 'eids.authority.property_help' : 'eids.authority.vehicle_help'));
            return null;
        }
        return { [field]: value };
    }
}
